import { DebouncedFunction, getThemeColor, HorizontalAlignment, Logger, StringUtil } from "@mcleod/core";
import { Button } from "../../components/button/Button";
import { ChangeEvent } from "../../events/ChangeEvent";
import { ClearButtonVisible } from "../../components/textbox/ClearButtonVisible";
import { Cursor } from "../../base/Cursor";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { DragEvent } from "../../events/DragEvent";
import { HorizontalSpacer } from "../../page/HorizontalSpacer";
import { Image } from "../../components/image/Image";
import { MouseEvent } from "../../events/MouseEvent";
import { Panel } from "../../components/panel/Panel";
import { PanelProps } from "../../components/panel/PanelProps";
import { Textbox } from "../.."; // Avoids circular dependency
import { ValidationResult } from "../../base/ValidationResult";

export class ImageZoomControls extends Panel {
    private static log: Logger;

    // Constants for zoom increment changes, min/max zoom values
    private readonly BUTTON_ZOOM_STEP = 0.2;
    private readonly SCROLL_ZOOM_STEP = 1.1;
    private readonly DEFAULT_ZOOM = 1.0; // 100%
    private readonly MAX_ZOOM = 5.0; // 500%
    private readonly MIN_ZOOM = 0.2; // 20%

    // Holds the current zoom value
    private zoomFactor: number = this.DEFAULT_ZOOM;

    // Component variables and current zoom factor value
    private buttonZoomReset: Button;
    private buttonZoomIn: Button;
    private buttonZoomOut: Button;
    private _image: Image;
    private textboxZoomPercent: Textbox;
    private debouncedApplyZoomFromTextbox = DebouncedFunction.create(() => this.applyZoomFromTextbox(), 300);
    
    // Variables used by the drag-to-pan listeners
    private isDragging = false;
    private startX: number;
    private startY: number;
    private translateX = 0;
    private translateY = 0;

    constructor(image: Image, props?: Partial<PanelProps>) {
        super({ margin: 0, padding: 0, rowBreakDefault: false, ...props });
        this.image = image;
        this.createButtonZoomIn();
        this.createTextboxZoomPercent();
        this.createButtonZoomOut();
        this.createButtonZoomReset();
        this.add(
            new HorizontalSpacer(),
            this.buttonZoomOut,
            this.textboxZoomPercent,
            this.buttonZoomIn,
            this.buttonZoomReset,
        );
    }

    private createButtonZoomIn() {
        this.buttonZoomIn = new Button({
            tooltip: `Zoom in ${this.BUTTON_ZOOM_STEP * 100}%`,
            caption: "+"
        });
        this.buttonZoomIn.addClickListener(() => this.zoomIn());
    }

    private createButtonZoomOut() {
        this.buttonZoomOut = new Button({
            tooltip: `Zoom out ${this.BUTTON_ZOOM_STEP * 100}%`,
            caption: "-"
        });
        this.buttonZoomOut.addClickListener(() => this.zoomOut());
    }

    private createButtonZoomReset() {
        this.buttonZoomReset = new Button({
            tooltip: `Reset zoom to ${this.DEFAULT_ZOOM * 100}%`,
            caption: "Reset"
        });
        this.buttonZoomReset.addClickListener(() => this.resetZoom());
    }

    private createTextboxZoomPercent() {
        this.textboxZoomPercent = new Textbox({
            captionVisible: false,
            width: 65,
            clearButtonVisible: ClearButtonVisible.NO,
            fontSize: "large",
            fontBold: true,
            align: HorizontalAlignment.CENTER
        });
        this.syncTextboxZoomPercent(); // Sets textbox's text from default zoom factor value
        this.textboxZoomPercent._inputDiv.style.backgroundColor = getThemeColor("primary.reverse");
        this.textboxZoomPercent._inputDiv.style.color = getThemeColor("default");
        this.textboxZoomPercent.addlValidationCallback = (): ValidationResult => {
            return this.validateZoomPercent();
        };
        this.textboxZoomPercent.addChangeListener((event: ChangeEvent) => this.textboxZoomPercentOnChange(event));
        this.textboxZoomPercent.addFocusListener(() => {
            // Remove % from the text
            this.textboxZoomPercent.text = StringUtil.removeNonDigits(this.textboxZoomPercent.text);
        });
        this.textboxZoomPercent.addBlurListener(() => {
            // If the user leaves the textbox and entered an invalid value, reset it to the current zoom factor so the
            // zoom won't change.
            let value = this.textboxZoomPercent.text;
            if (this.validateZoomPercent()?.isValid === false) {
                value = String(this.zoomFactor * 100);
            }
            this.textboxZoomPercent.text = `${value}%`;
        });
    }

    private validateZoomPercent(): ValidationResult {
        const zoomPercentNumeric = this.textboxZoomPercentNumeric / 100;
        if (isNaN(zoomPercentNumeric) || zoomPercentNumeric < this.MIN_ZOOM || zoomPercentNumeric > this.MAX_ZOOM) {
            const invalidResult = {
                component: this.textboxZoomPercent,
                isValid: false,
                validationMessage: `Please enter a valid percentage between ${this.MIN_ZOOM}% and ${this.MAX_ZOOM}%`
            }
            this.log.debug(this, "Entered zoom percentage is invalid: %o", zoomPercentNumeric);
            return invalidResult;
        }
        this.log.debug(this, "Entered zoom percentage is valid: %o", zoomPercentNumeric);
        return null;
    }

    private get textboxZoomPercentNumeric(): number {
        const zoomPercent = this.textboxZoomPercent.text.replace(/\%/g, "");
        return Number(zoomPercent);
    }

    /**
     * When the user types in a zoom percentage, apply that percentage.  But first wait a few milliseconds to make
     * sure they finished typing.
     */
    private textboxZoomPercentOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange !== true) {
            return;
        }
        this.debouncedApplyZoomFromTextbox();
    }

    private applyZoomFromTextbox() {
        if (this.validateZoomPercent()?.isValid !== false) {
            this.zoomFactor = this.roundToHundredth(this.textboxZoomPercentNumeric / 100);
            this.log.debug(this, "Applying zoom percentage value from textbox: %o", this.zoomFactor);
            this.applyZoomNonScrollWheel();
        }
    }

    private syncTextboxZoomPercent() {
        if (!this.textboxZoomPercent.hasFocus()) {
            this.textboxZoomPercent.text = `${Math.round(this.zoomFactor * 100)}%`;
        }
    }

    get image(): Image {
        return this._image;
    }

    private set image(value: Image) {
        this._image = value;
    }

    public zoomIn(): void {
        if (this.zoomFactor < this.MAX_ZOOM) {
            this.log.debug(this, "Zooming in via button");
            const newZoomFactor = this.roundToHundredth(this.zoomFactor + this.BUTTON_ZOOM_STEP);
            this.zoomFactor = this.checkZoomLimits(newZoomFactor);
            this.applyZoomNonScrollWheel();
        } else {
            this.log.debug(this, "Not Zooming out via button, maximum zoom already reached/exceeded");
        }
    }

    public zoomOut(): void {
        if (this.zoomFactor > this.MIN_ZOOM) {
            this.log.debug(this, "Zooming out via button");
            const newZoomFactor = this.roundToHundredth(this.zoomFactor - this.BUTTON_ZOOM_STEP);
            this.zoomFactor = this.checkZoomLimits(newZoomFactor);
            this.applyZoomNonScrollWheel();
        } else {
            this.log.debug(this, "Not Zooming out via button, minimum zoom already reached/exceeded");
        }
    }

    public resetZoom(): void {
        this.log.debug(this, "Resetting zoom");
        this.zoomFactor = this.DEFAULT_ZOOM;
        this.translateX = 0;
        this.translateY = 0;
        this.applyZoomNonScrollWheel();
    }

    private enableButtons() {
        this.buttonZoomIn.enabled = this.zoomFactor < this.MAX_ZOOM;
        this.buttonZoomOut.enabled = this.zoomFactor > this.MIN_ZOOM;
    }

    enableAll(value: boolean) {
        this.components.forEach(component => component.enabled = value);
    }

    private applyZoomNonScrollWheel(): void {
        try {
            this.log.debug(this, "Applying button zoom: Zoom Factor: %o, translateX: %o, translateY: %o",
                this.zoomFactor, this.translateX, this.translateY);
            if (this.image != null) {
                // Always use center origin for button zooming
                this.image.style.transformOrigin = 'center center';

                if (this.translateX !== 0 || this.translateY !== 0) {
                    // If already panned, maintain the same relative position
                    this.image.style.transform = 
                        `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoomFactor})`;
                } else {
                    // No translation yet, just apply centered zoom
                    this.image.style.transform = `scale(${this.zoomFactor})`;
                }
            }
        } finally {
            this.log.debug(this,
                "Finished applying button zoom: Zoom Factor: %o, translateX: %o, translateY: %o",
                this.zoomFactor, this.translateX, this.translateY);
            this.syncTextboxZoomPercent();
            this.syncControls();
        }
    }

    private syncControls() {
        this.enableButtons();
        this.syncMouseWheelZoom();
        this.syncDragToPan();
    }

    private syncMouseWheelZoom(): void {
        if (this.image?.isEmpty() === false) {
            this.image._element.addEventListener("wheel", this.zoomOnMouseWheel);
        } else {
            this.image?._element.removeEventListener("wheel", this.zoomOnMouseWheel);
        }
    }

    private zoomOnMouseWheel = (event: WheelEvent) => {
        event.preventDefault();

        // Only process with Control or Windows/Command key pressed
        if (!event.ctrlKey && !event.metaKey) {
            return;
        }

        // Determine zoom direction
        const isZoomingIn = (event.deltaY < 0) ? true : false;

        this.log.debug(this, "Zooming %o from mouse wheel", isZoomingIn ? "in" : "out");

        // Use smaller zoom factor when scrolling (smaller than the fixed value used by button-driven zoom in/out)
        let scrollingZoomFactor: number;
        if (isZoomingIn) {
            // When scrolling in, use factor of 1.1
            scrollingZoomFactor = this.SCROLL_ZOOM_STEP;
        } else {
            // When scrolling out, use reciprocal value
            scrollingZoomFactor = 1 / this.SCROLL_ZOOM_STEP;
        }
        // Calculate new scale, round to nearest tenth, and check bounds
        let newZoomFactor = this.zoomFactor * scrollingZoomFactor;
        newZoomFactor = this.roundToHundredth(newZoomFactor);
        newZoomFactor = this.checkZoomLimits(newZoomFactor);
        this.log.debug(this, "New zoom factor: %o", this.zoomFactor);
        
        // If we're already at the limit and trying to go further, just exit
        if (newZoomFactor === this.zoomFactor) {
            return;
        }

        if (this.image != null) {
            try {
                // Get the container rect (in this case, the image's parent container)
                const containerRect = this.image._element.parentElement.getBoundingClientRect();
                
                // Mouse position relative to the container
                const mouseX = event.clientX - containerRect.left;
                const mouseY = event.clientY - containerRect.top;
                
                // Calculate the point under cursor in the original, unscaled coordinates
                const prevX = (mouseX - this.translateX) / this.zoomFactor;
                const prevY = (mouseY - this.translateY) / this.zoomFactor;
                
                // Update the zoom factor
                this.zoomFactor = newZoomFactor;
                
                // Calculate new offsets to keep mouse position fixed over the same image point
                this.translateX = mouseX - prevX * this.zoomFactor;
                this.translateY = mouseY - prevY * this.zoomFactor;
                
                // Apply the transformation
                this.image._element.style.transformOrigin = 'top left';
                this.image._element.style.transform = 
                    `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoomFactor})`;
            } finally {
                this.syncTextboxZoomPercent();
                this.syncControls();
            }
        }
    };

    private syncDragToPan(): void {
        if (this.image != null) {
            // Defeat default browser behavior of an image being draggable, regardless of if currently zoomed in
            this.image.addDragStartListener(this.dragToPanOnDragStart);
        }

        if (this.image?.isEmpty() === false) {
            this.image.addMouseDownListener(this.dragToPanOnMouseDown);
            this.image.addMouseMoveListener(this.dragToPanOnMouseMove);
            this.image.addMouseUpListener(this.dragToPanOnMouseUp);
            this.image.addMouseLeaveListener(this.dragToPanOnMouseLeave);
            this.image.cursor = Cursor.GRAB;
        } else if (this.image != null) {
            this.image.removeMouseDownListener(this.dragToPanOnMouseDown);
            this.image.removeMouseMoveListener(this.dragToPanOnMouseMove);
            this.image.removeMouseUpListener(this.dragToPanOnMouseUp);
            this.image.removeMouseLeaveListener(this.dragToPanOnMouseLeave);
            this.image.cursor = Cursor.DEFAULT;
        }
    }

    private dragToPanOnMouseDown = (event: MouseEvent) => {
        if (this.image?.isEmpty() === false) {
            //Prevent default browser behavior of the image being draggable
            event.domEvent.preventDefault();

            this.isDragging = true;
            this.startX = event.domEvent.clientX - this.translateX;
            this.startY = event.domEvent.clientY - this.translateY;
            this.image.cursor = Cursor.GRABBING;
        }
    };

    // Defeat default browser behavior is for images to be draggable...need to defeat that for drag-to-pan
    private dragToPanOnDragStart = (event: DragEvent) => {
        event.domEvent.preventDefault();
    };

    private dragToPanOnMouseMove = (event: MouseEvent) => {
        if (this.isDragging) {
            this.translateX = event.domEvent.clientX - this.startX;
            this.translateY = event.domEvent.clientY - this.startY;
            this.image._element.style.transform =
                `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoomFactor})`;
        }
    };

    private dragToPanOnMouseUp = () => {
        this.isDragging = false;
        this.image.cursor = Cursor.GRAB;
    };

    private dragToPanOnMouseLeave = () => {
        if (this.isDragging) {
            this.isDragging = false;
            this.image.cursor = Cursor.GRAB;
        }
    };

    private checkZoomLimits(newZoomFactor: number): number {
        // Apply min/max constraints
        if (newZoomFactor > this.MAX_ZOOM) {
            this.log.debug(this, "Reached maximum zoom limit");
            return this.MAX_ZOOM;
        } else if (newZoomFactor < this.MIN_ZOOM) {
            this.log.debug(this, "Reached minimum zoom limit");
            return this.MIN_ZOOM;
        }
        return newZoomFactor;
    }

    private roundToHundredth(value: number): number {
        return Math.round(value * 100) / 100;
    }

    private get log(): Logger {
        if (ImageZoomControls.log == null) {
            ImageZoomControls.log =
                DesignableObjectLogManager.getLogger("components.image.ImageZoomControls");
        }
        return ImageZoomControls.log;
    }
}
